import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, ClipboardList, Shield, HelpCircle, Download, Layers } from 'lucide-react';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';
import ReferralFormModal from '../../components/ReferralFormModal';
import FAQRichSnippet from '../../components/FAQRichSnippet';
import { downloadBlankIntakePacket } from '../../utils/intakePacketPdf';

const QUICK_LINKS = [
  { icon: Layers, title: 'Services Available for Referral', description: 'See the full range of OMHC and PRP services we accept referrals for.', href: '/services/comprehensive-mental-health' },
  { icon: ClipboardList, title: 'Referral Requirements', description: 'Required provider info, functional criteria, and attachments.', href: '/providers/referral-requirements' },
  { icon: Shield, title: 'Insurance & Payer Info', description: 'Medicaid, Medicare, and self-pay options accepted.', href: '/insurance' },
  { icon: HelpCircle, title: 'Frequently Asked Questions', description: 'Common questions about our programs and intake process.', href: '/faq' }
];

const PROVIDER_FAQS = [
  {
    q: 'What information do I need to submit a referral?',
    a: "You'll need the client's contact information, current diagnosis, and reason for referral, plus your provider credentials and NPI number. See our Referral Requirements page for the full checklist."
  },
  {
    q: 'How long does it take to hear back after submitting a referral?',
    a: 'Our intake team typically reviews new referrals and reaches out to schedule intake within one business week.'
  },
  {
    q: 'Can I submit a referral for both OMHC and PRP services?',
    a: 'Yes — select "Both OMHC and PRP" as the referral category on the form if the client may benefit from both programs.'
  },
  {
    q: 'Do you accept referrals for minors?',
    a: 'Yes, we provide OMHC and PRP services for both adults and minors.'
  },
  {
    q: "What if I don't have all the required attachments yet?",
    a: 'Submit what you have — our intake team can follow up with you for any missing documentation before intake is finalized.'
  },
  {
    q: "Is there a fax or paper option if I can't use the online form?",
    a: 'Yes — download a blank intake packet to complete and fax, or contact our intake team directly by phone.'
  }
];

const ProviderResourcesPage = () => {
  const [showReferralForm, setShowReferralForm] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Provider Resources"
        description="Resources for referring providers: submit a referral, review requirements, preview the client intake packet, and find insurance and FAQ information."
        path="/providers/resources"
      />
      <Navigation />

      {/* Hero */}
      <div className="bg-gradient-to-br from-slate-700 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="provider-resources-title">
            Provider Resources
          </h1>
          <p className="text-xl md:text-2xl text-slate-200 max-w-3xl">
            Everything referring providers and agencies need in one place.
          </p>
        </div>
      </div>

      {/* Quick Links */}
      <div className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-gray-700 max-w-3xl mx-auto text-center mb-10">
            Whether you're a case manager, discharge planner, school counselor, or primary care provider, the links
            below cover everything you need to make a confident referral to SDORA Wellbeing: what services we offer,
            what information to gather beforehand, how insurance is handled, and answers to the questions referring
            partners ask most often.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {QUICK_LINKS.map(({ icon: Icon, title, description, href }) => (
              <Link
                key={title}
                to={href}
                className="flex items-start gap-4 p-6 bg-white border border-gray-200 rounded-xl hover:shadow-lg hover:border-gray-300 transition-all group"
                data-testid={`provider-resource-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="p-3 rounded-xl bg-slate-100 text-slate-700 flex-shrink-0">
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900 mb-1">{title}</h2>
                  <p className="text-sm text-gray-600">{description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Downloadable Forms */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <Download className="w-6 h-6 text-slate-700" />
            <h2 className="text-2xl font-bold text-gray-900">Preview the Client Intake Packet</h2>
          </div>
          <p className="text-gray-700 mb-6">
            Download a blank copy of the intake and consent packet your referred clients will complete, so you know
            what information to prepare in advance.
          </p>
          <button
            onClick={downloadBlankIntakePacket}
            className="px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center gap-2"
            data-testid="provider-resources-download-packet-btn"
          >
            <Download className="w-4 h-4" /> Download Blank Intake Packet
          </button>
        </div>
      </div>

      {/* Provider FAQ */}
      <div className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-2">
            <HelpCircle className="w-6 h-6 text-slate-700" />
            <h2 className="text-2xl font-bold text-gray-900">Common Referral Questions</h2>
          </div>
          <FAQRichSnippet faqs={PROVIDER_FAQS} title="" />
        </div>
      </div>

      {/* Submit CTA */}
      <div className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to Refer a Client?</h2>
          <p className="text-gray-700 mb-6">Submit an OMHC or PRP referral using our secure online form.</p>
          <button
            onClick={() => setShowReferralForm(true)}
            className="px-6 py-3 bg-blue-700 text-white rounded-lg font-semibold hover:bg-blue-800 transition-colors inline-flex items-center gap-2"
            data-testid="provider-resources-submit-referral-btn"
          >
            <Send className="w-4 h-4" /> Submit a Referral
          </button>
        </div>
      </div>

      <Footer />

      {showReferralForm && <ReferralFormModal onClose={() => setShowReferralForm(false)} />}
    </div>
  );
};

export default ProviderResourcesPage;
